import { create } from "zustand";
import axios from "axios";

// API 기본 설정 - backend-api-v2
const api = axios.create({
	baseURL: import.meta.env.VITE_API_URL || 'http://localhost:8091/api/v1',
});

// 인터셉터 설정 - 모든 요청에 토큰 추가
api.interceptors.request.use(
	(config) => {
		const token = JSON.parse(localStorage.getItem("auth-storage"))?.state
			?.token;
		if (token) {
			config.headers.Authorization = `Bearer ${token}`;
		}
		return config;
	},
	(error) => Promise.reject(error)
);

// 기존 backend-api URL (마스크 등 미구현 기능용)
const LEGACY_API_URL = `${import.meta.env.VITE_SERVER_URL}`;

export const useTicketStore = create((set, get) => ({
	// 상태 관리
	leftTickets: [],
	rightTickets: [],
	ticketDetails: null,
	seatAssignments: [],
	selectedSeats: [],
	isLoading: false,
	error: null,

	// 승차권 등록 관련 상태
	normalTickets: [],
	specialTickets: [],
	ocrResponses: {},
	normalOcrResponses: {},
	ticketCounter: 0,
	normalTicketCounter: 0,

	// 승차권 업로드 및 OCR 처리
	processTicketFile: async (file, isNormal = false) => {
		const {
			ticketCounter,
			normalTicketCounter,
			ocrResponses,
			normalOcrResponses,
		} = get();
		const currentCounter = isNormal
			? normalTicketCounter + 1
			: ticketCounter + 1;

		// 카운터 업데이트
		if (isNormal) {
			set({ normalTicketCounter: currentCounter });
		} else {
			set({ ticketCounter: currentCounter });
		}

		// 파일 업로드를 위한 FormData 생성
		const formData = new FormData();
		formData.append("file", file);

		try {
			// OCR API 호출 (v2)
			const endpoint = isNormal ? "/tickets/ocr-normal" : "/tickets/ocr";
			const response = await api.post(`${endpoint}`, formData, {
				headers: {
					"Content-Type": "multipart/form-data",
				},
			});

			// OCR 결과 저장
			if (isNormal) {
				set({
					normalOcrResponses: {
						...normalOcrResponses,
						[currentCounter]: response.data,
					},
				});
			} else {
				set({
					ocrResponses: {
						...ocrResponses,
						[currentCounter]: response.data,
					},
				});
			}

			return { success: true, counter: currentCounter };
		} catch (error) {
			console.error("OCR 요청 실패:", error);
			return {
				success: false,
				counter: currentCounter,
				error: error.message,
			};
		}
	},

	// 티켓 데이터 저장
	saveTicketData: async (data, isNormal = false) => {
		try {
			set({ isLoading: true, error: null });
			let response;
			
			// FormData 객체인 경우 (이미지 업로드)
			if (data instanceof FormData) {
				response = await api.post('/tickets', data, {
					headers: {
						'Content-Type': 'multipart/form-data'
					}
				});

				set({ isLoading: false });
				return [{
					success: true,
					data: response.data.ticket || response.data,
					index: 0
				}];
			}
			// 기존 방식 (배열 처리)
			else {
				const promises = data.map((ticket) => {
					return api.post("/tickets", ticket);
				});

				const results = await Promise.all(promises);
				set({ isLoading: false });
				return results.map((response) => ({
					success: true,
					data: response.data.ticket,
					index: response.data.ticket.index,
				}));
			}
		} catch (error) {
			console.error("티켓 저장 실패:", error);
			set({ isLoading: false, error: error.response?.data?.error || '티켓 저장 중 오류가 발생했습니다.' });
			return [{ success: false, error: error.response?.data?.error || '티켓 저장 중 오류가 발생했습니다.' }];
		}
	},

	// 티켓 데이터 저장 (이미지 포함)
	saveTicketDataWithImage: async (data) => {
		try {
			set({ isLoading: true, error: null });

			const response = await api.post('/tickets', data, {
				headers: {
					'Content-Type': 'multipart/form-data'
				}
			});

			set({ isLoading: false });
			return [{
				success: true,
				data: response.data.ticket || response.data,
				index: 0
			}];
		} catch (error) {
			console.error("티켓 저장 실패:", error);
			set({ isLoading: false, error: error.response?.data?.error || '티켓 저장 중 오류가 발생했습니다.' });
			return [{ success: false, error: error.response?.data?.error || '티켓 저장 중 오류가 발생했습니다.' }];
		}
	},

	// 티켓 업데이트 (v2)
	updateTicket: async (data, file) => {
		try {
			set({ isLoading: true, error: null });

			let formData = new FormData();

			if (file) {
				formData.append('updated_image', file);
			}

			formData.append('data', JSON.stringify(data));

			const response = await api.put('/tickets/update-detail', formData, {
				headers: {
					'Content-Type': 'multipart/form-data'
				}
			});
			
			set({ isLoading: false });
			return {
				success: true,
				data: response.data
			};
		} catch (error) {
			console.error('티켓 업데이트 실패:', error);
			set({ isLoading: false, error: error.response?.data?.error || '티켓 업데이트 중 오류가 발생했습니다.' });
			return { 
				success: false, 
				error: error.response?.data?.error || '티켓 업데이트 중 오류가 발생했습니다.' 
			};
		}
	},

	// 티켓 아이템 추가 (v2 - 티켓 생성으로 대체)
	addTicketItem: async (ticketData) => {
		try {
			set({ isLoading: true, error: null });

			// v2에서는 티켓 생성 API 사용
			const response = await api.post('/tickets', ticketData);
			
			set({ isLoading: false });
			return {
				success: true,
				data: response.data
			};
		} catch (error) {
			console.error('티켓 아이템 추가 실패:', error);
			set({ isLoading: false, error: error.response?.data?.error || '티켓 아이템 추가 중 오류가 발생했습니다.' });
			return { 
				success: false, 
				error: error.response?.data?.error || '티켓 아이템 추가 중 오류가 발생했습니다.' 
			};
		}
	},

	// 티켓 삭제 (v2)
	deleteTickets: async (ticketIdList) => {
		try {
			set({ isLoading: true, error: null });
			const response = await api.post('/tickets/delete-batch', {
				ticket_id_list: ticketIdList
			});
			
			set({ isLoading: false });
			return {
				success: true,
				data: response.data
			};
		} catch (error) {
			console.error('티켓 삭제 실패:', error);
			set({ isLoading: false, error: error.response?.data?.error || '티켓 삭제 중 오류가 발생했습니다.' });
			return { 
				success: false, 
				error: error.response?.data?.error || '티켓 삭제 중 오류가 발생했습니다.' 
			};
		}
	},

	// 티켓 아이템 삭제 (v2)
	deleteTicketItem: async (ticketItemId) => {
		try {
			set({ isLoading: true, error: null });
			const response = await api.delete('/tickets/delete-item', {
				data: { ticket_item_id: ticketItemId }
			});
			
			set({ isLoading: false });
			return {
				success: true,
				data: response.data
			};
		} catch (error) {
			console.error('티켓 아이템 삭제 실패:', error);
			set({ isLoading: false, error: error.response?.data?.error || '티켓 아이템 삭제 중 오류가 발생했습니다.' });
			return { 
				success: false, 
				error: error.response?.data?.error || '티켓 아이템 삭제 중 오류가 발생했습니다.' 
			};
		}
	},

	// OCR 응답 가져오기
	getOcrResponse: (counter, isNormal = false) => {
		const { ocrResponses, normalOcrResponses } = get();
		return isNormal ? normalOcrResponses[counter] : ocrResponses[counter];
	},

	// OCR 응답 업데이트
	updateOcrResponse: (counter, data, isNormal = false) => {
		if (isNormal) {
			set((state) => ({
				normalOcrResponses: {
					...state.normalOcrResponses,
					[counter]: data,
				},
			}));
		} else {
			set((state) => ({
				ocrResponses: {
					...state.ocrResponses,
					[counter]: data,
				},
			}));
		}
	},

	// 일자별 승차권 보유 현황 조회 (v2)
	getSeatReservationStatus: async (departureTime, type, query = null) => {
		set({ isLoading: true, error: null });
		try {
			const response = await api.get(
				"/reservations/seat-status",
				{
					params: {
						departure_time: departureTime,
						type: type,
						query: query || null,
					},
				}
			);

			if (type === "left") {
				set({ leftTickets: response.data });
			} else if (type === "right") {
				set({ rightTickets: response.data });
			}

			set({ isLoading: false });
			return { success: true, data: response.data };
		} catch (err) {
			console.error(`Error fetching ${type} tickets:`, err);
			const errorMessage =
				err.response?.data?.error ||
				`${
					type === "left" ? "왕편" : "복편"
				} 티켓 정보를 가져오는 중 오류가 발생했습니다.`;
			set({
				isLoading: false,
				error: errorMessage,
			});
			return { success: false, error: errorMessage };
		}
	},

	// 양쪽(왕편/복편) 모두 조회 (v2)
	getSeatReservationStatusBoth: async (departureTime, query = null) => {
		set({ isLoading: true, error: null });
		try {
			// 왕편 조회
			const leftResponse = await api.get(
				"/reservations/seat-status",
				{
					params: {
						departure_time: departureTime,
						type: "left",
						query: query || null,
					},
				}
			);

			// 복편 조회
			const rightResponse = await api.get(
				"/reservations/seat-status",
				{
					params: {
						departure_time: departureTime,
						type: "right",
						query: query || null,
					},
				}
			);

			set({
				leftTickets: leftResponse.data,
				rightTickets: rightResponse.data,
				isLoading: false,
			});

			return {
				success: true,
				data: {
					left: leftResponse.data,
					right: rightResponse.data,
				},
			};
		} catch (err) {
			console.error("Error fetching tickets:", err);
			const errorMessage =
				err.response?.data?.error ||
				"티켓 정보를 가져오는 중 오류가 발생했습니다.";
			set({
				isLoading: false,
				error: errorMessage,
			});
			return { success: false, error: errorMessage };
		}
	},

	// 상태 초기화
	resetTickets: () => {
		set({
			leftTickets: [],
			rightTickets: [],
			error: null,
		});
	},

	// 로딩 상태 확인
	getLoadingState: () => get().isLoading,

	// 에러 상태 확인
	getErrorState: () => get().error,

	// API 인스턴스 제공
	getApi: () => api,

	// 좌석 배치 정보 조회 (v2)
	getSeatAssignments: async (
		ticketId,
		trainNumber,
		departureDate,
		departureStation,
		arrivalStation
	) => {
		set({ isLoading: true, error: null });
		try {
			// v2 엔드포인트 사용
			const response = await api.get(
				`/reservations/seat-status-detail?ticket_item_ids=${ticketId}`
			);

			// API 응답 데이터 처리
			const responseData = response.data;
			const trainKey = Object.keys(responseData)[0]; // 첫 번째 키(열차 ID)

			if (!trainKey) {
				throw new Error("좌석 정보가 없습니다.");
			}

			const trainData = responseData[trainKey];
			const seatData = trainData.data;

			// 좌석 데이터를 배열 형태로 변환
			const formattedSeats = [];
			Object.keys(seatData).forEach((seatNumber) => {
				const seatInfo = seatData[seatNumber];
				if (seatInfo.id) {
					formattedSeats.push({
						id: seatInfo.id,
						seat_number: seatNumber,
						passenger_name: seatInfo.seat || "-",
						status: seatInfo.seat ? "예약완료" : "예약가능",
					});
				} else if (seatNumber) {
					// id가 없는 경우도 추가 (예약 가능한 좌석)
					formattedSeats.push({
						id: seatNumber, // 임시 ID로 좌석번호 사용
						seat_number: seatNumber,
						passenger_name: "-",
						status: "예약가능",
					});
				}
			});

			// 열차 정보 추출
			const ticketDetails = {
				train_number: trainNumber,
				departure_date: departureDate,
				departure_station: departureStation,
				arrival_station: arrivalStation,
			};

			set({
				seatAssignments: formattedSeats,
				ticketDetails: ticketDetails,
				isLoading: false,
			});

			return {
				success: true,
				data: formattedSeats,
				originalData: responseData,
			};
		} catch (err) {
			console.error("Error fetching seat assignments:", err);
			set({
				isLoading: false,
				error:
					err.response?.data?.message ||
					"좌석 배치 정보를 불러오는 중 오류가 발생했습니다.",
			});
			return { success: false, error: err };
		}
	},

	// 좌석 배치도 레이아웃 조회 (v2)
	getSeatmapLayout: async (trainTypeCode, variant = null) => {
		try {
			const params = {};
			if (trainTypeCode) {
				params.train_type_code = trainTypeCode;
			}
			if (variant) {
				params.variant = variant;
			}

			const response = await api.get("/seatmaps/layout", { params });
			const data = response.data;

			return {
				success: data?.success !== false,
				layout: data?.layout || null,
			};
		} catch (err) {
			console.error("Error fetching seatmap layout:", err);
			return {
				success: false,
				error:
					err.response?.data?.message ||
					"좌석 배치도 레이아웃을 불러오는 중 오류가 발생했습니다.",
			};
		}
	},

	// 좌석 선택 관리
	selectSeat: (seatId) => {
		const { selectedSeats } = get();
		if (selectedSeats.includes(seatId)) {
			set({ selectedSeats: selectedSeats.filter((id) => id !== seatId) });
		} else {
			set({ selectedSeats: [...selectedSeats, seatId] });
		}
	},

	// 상태 초기화
	resetTicketDetails: () => {
		set({
			ticketDetails: null,
			seatAssignments: [],
			selectedSeats: [],
			error: null,
		});
	},
	
	// 승차권 등록 현황 조회 (v2)
	getAllTickets: async (startDate, endDate, station) => {
		set({ isLoading: true, error: null });
		try {
			const params = {
				skip: 0,
				limit: 1000,
			};

			// 날짜 필터 추가
			if (startDate) {
				params.start_date = startDate;
			}
			if (endDate) {
				params.end_date = endDate;
			}

			const response = await api.get('/tickets', { params });

			// API 응답 데이터를 프론트엔드 형식으로 변환
			let transformedData = response.data.map(ticket => {
				const items = ticket.ticket_items || [];

				// ticket_items에서 데이터 추출
				const reservationNumbers = items.map(item => item.reservation_number).filter(Boolean);
				const departureTimes = items.map(item => {
					if (item.departure_time) {
						const dt = new Date(item.departure_time);
						return dt.toLocaleDateString('ko-KR', { month: '2-digit', day: '2-digit' });
					}
					return null;
				}).filter(Boolean);
				const trainCodes = items.map(item => item.train_code).filter(Boolean);
				const stations = items.map(item => {
					if (item.departure_station && item.arrival_station) {
						return `${item.departure_station} - ${item.arrival_station}`;
					}
					return null;
				}).filter(Boolean);

				// 좌석 배정 현황 계산
				const totalSeats = items.reduce((sum, item) => sum + (item.seat_infos?.length || 0), 0);
				const assignedSeats = items.reduce((sum, item) => {
					return sum + (item.seat_infos?.filter(seat => seat.user_id)?.length || 0);
				}, 0);
				const allocationStatus = totalSeats > 0 ? `${assignedSeats}/${totalSeats}` : '';

				return {
					...ticket,
					ticket_id: ticket.id, // id -> ticket_id 매핑
					reservation_number: reservationNumbers.join('\n'),
					departure_times: departureTimes.join('\n'),
					train_codes: trainCodes.join('\n'),
					departure_arrival_stations: stations.join('\n'),
					allocation_status: allocationStatus,
				};
			});

			// 출/도착역 필터링 (클라이언트 사이드)
			if (station && station.trim()) {
				const keyword = station.trim().toLowerCase();
				transformedData = transformedData.filter(ticket => {
					const stationText = (ticket.departure_arrival_stations || '').toLowerCase();
					return stationText.includes(keyword);
				});
			}

			set({
				statusTickets: transformedData,
				isLoading: false
			});

			return { success: true, data: transformedData };
		} catch (err) {
			console.error('Error fetching tickets:', err);
			const errorMessage = err.response?.data?.error || '승차권 정보를 가져오는 중 오류가 발생했습니다.';
			set({
				isLoading: false,
				error: errorMessage,
			});
			return { success: false, error: errorMessage };
		}
	},

	// 승차권 상세 정보 조회 (v2)
	getTicketDetail: async (id) => {
		try {
			set({ isLoading: true, error: null });

			const extractDateTimeParts = (value) => {
				if (!value) {
					return { date: "", time: "" };
				}

				if (value instanceof Date) {
					const year = value.getFullYear();
					const month = `${value.getMonth() + 1}`.padStart(2, "0");
					const day = `${value.getDate()}`.padStart(2, "0");
					const hours = `${value.getHours()}`.padStart(2, "0");
					const minutes = `${value.getMinutes()}`.padStart(2, "0");
					return { date: `${year}-${month}-${day}`, time: `${hours}:${minutes}` };
				}

				const stringValue = String(value).trim();
				const dateTimeMatch = stringValue.match(
					/(\d{4}-\d{2}-\d{2})(?:[T\s](\d{2}:\d{2}))?/
				);

				if (dateTimeMatch) {
					return {
						date: dateTimeMatch[1] || "",
						time: dateTimeMatch[2] || "",
					};
				}

				const timeMatch = stringValue.match(/(\d{2}:\d{2})/);
				return {
					date: "",
					time: timeMatch ? timeMatch[1] : "",
				};
			};

			const response = await api.get(`/tickets/${id}`);
			const ticket = response.data;

			// API 응답을 EditTicketModal이 기대하는 형식으로 변환
			// ticket_items 배열을 펼쳐서 각 아이템에 티켓 정보를 포함
			const transformedData = (ticket.ticket_items || []).map((item, index) => {
				const departureTimeParts = extractDateTimeParts(item.departure_time);
				const arrivalTimeParts = extractDateTimeParts(item.arrival_time);

				return {
					id: ticket.id,
					ticket_id: ticket.id,
					ticket_item_id: item.id,
					reservation_number: item.reservation_number || '',
					departure_station: item.departure_station || '',
					arrival_station: item.arrival_station || '',
					departure_date: departureTimeParts.date,
					departure_time: departureTimeParts.time,
					arrival_time: arrivalTimeParts.time,
					train_code: item.train_code || '',
					train_type_code: item.train_type_code || '',
					ticket_type: ticket.ticket_type || '일반승차권',
					image_url: ticket.image_url || '',
					s3_url: ticket.image_url || '',
					seat_info_list: (item.seat_infos || []).map(seat => ({
						seat_info_id: seat.id,
						seat_code: `${seat.train_car_number || ''} ${seat.seat_code || ''}`.trim(),
						train_car_number: seat.train_car_number || '',
						user_name: seat.user_name || '',
						user_phone: seat.user_phone || '',
						user_id: seat.user_id || null,
						user_reservation_cnt: seat.user_reservation_cnt || 0,
					})),
				};
			});

			set({ ticketDetails: transformedData, isLoading: false });
			return { success: true, data: transformedData };
		} catch (err) {
			console.error('Error fetching ticket detail:', err);
			const errorMessage = err.response?.data?.error?.message || err.message || '승차권 상세 정보를 가져오는 중 오류가 발생했습니다.';
			set({
				isLoading: false,
				error: errorMessage,
			});
			return { success: false, error: errorMessage };
		}
	},

	// 마스크 데이터 로드 (v2 API)
	loadMaskData: async (ticketId) => {
		try {
			set({ isLoading: true, error: null });
			const response = await api.get(`/tickets/mask/${ticketId}`);

			if (response.status === 200 && response.data) {
				return response.data.masks || [];
			}
			return [];
		} catch (error) {
			console.error("마스크 데이터 로드 중 오류 발생:", error);
			set({ error: "마스크 데이터를 불러오는 데 실패했습니다." });
			return [];
		} finally {
			set({ isLoading: false });
		}
	},

	// 마스크 데이터 저장 (v2 API)
	saveMaskData: async (ticketId, masks) => {
		try {
			set({ isLoading: true, error: null });
			const response = await api.put(`/tickets/mask/${ticketId}`, {
				ticket_id: ticketId,
				masks: masks,
			});

			if (response.status === 200) {
				return {
					success: true,
					message: "마스크가 성공적으로 저장되었습니다!",
				};
			} else {
				return {
					success: false,
					message:
						"마스크 저장 중 오류가 발생했습니다. 다시 시도해주세요.",
				};
			}
		} catch (error) {
			console.error("마스크 저장 중 오류 발생:", error);
			set({ error: "마스크 저장 중 오류가 발생했습니다." });
			return {
				success: false,
				message:
					"마스크 저장 중 오류가 발생했습니다. 다시 시도해주세요.",
			};
		} finally {
			set({ isLoading: false });
		}
	},
}));
